<template>
  <div class="mobile-container" :style="colourVarStyle">
    <template v-if="primaryAppLoaded">
      <DebugConsole />
      <UserDetailsModal />
      <SplashScreenModal />
      <FaqModal />
      <SearchModal />
      <MobileFilterByCategoryModal />
      <ResizableSplitContainer>
        <template slot="top">
          <Spinner v-if="!activeAppLoaded" />
          <MobileStory />
          <CarouselTabs cols="12" />
        </template>
        <template slot="bottom">
          <Spinner v-if="!activeAppLoaded" />
          <template v-else>
            <ClickableBreadcrumbs v-if="breadcrumbsEnabled" />
            <MobileVisualisationView />
          </template>
        </template>
      </ResizableSplitContainer>
    </template>
    <Spinner v-else />
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'

import ResizableSplitContainer from '@/components/public/mobile/ResizableSplitContainer.vue'
import DebugConsole from '@/components/development/DebugConsole.vue'
import Spinner from '@/components/public/Spinner.vue'
import MobileVisualisationView from '@/components/public/mobile/MobileVisualisationView.vue'
import MobileStory from '@/components/public/mobile/MobileStory.vue'
import SplashScreenModal from '@/components/public/SplashScreenModal.vue'
import CarouselTabs from '@/components/public/CarouselTabs/CarouselTabs.vue'
import SearchModal from '@/components/public/SearchModal.vue'
import FaqModal from '@/components/public/FaqModal.vue'
import ClickableBreadcrumbs from '@/components/public/ClickableBreadcrumbs.vue'
import MobileFilterByCategoryModal from '@/components/public/FilterByCategory/MobileFilterByCategoryModal.vue'
import UserDetailsModal from '@/components/public/UserDetailsModal.vue'
import TTReader from '@/components/TipTap/wrappers/TTReader.vue'
import { StyleValue } from 'vue/types/jsx'
import { StoryKeys } from '@/types'

const components = {
  ResizableSplitContainer,
  DebugConsole,
  ClickableBreadcrumbs,
  Spinner,
  MobileVisualisationView,
  MobileStory,
  SplashScreenModal,
  CarouselTabs,
  SearchModal,
  TTReader,
  FaqModal,
  MobileFilterByCategoryModal,
  UserDetailsModal,
}

@Component({ components })
export default class MobileView extends Vue {
  get primaryAppLoaded() {
    return this.$typedStore.primary.app.appLoaded
  }

  get activeAppLoaded() {
    return this.$typedStore.activeVisualisation.appLoaded
  }

  get breadcrumbsEnabled() {
    return !this.$typedStore.primary.app.config.appConfig.disableMobileBreadcrumb
  }

  get activeTab() {
    return this.$typedStore.public.display.activeTab
  }

  get path() {
    let path: string | undefined
    if (this.$typedStore.activeVisualisation.isPrimaryApp || this.originalTabColours) {
      path = this.activeTab?.path
    } else if (this.$typedStore.activeVisualisation.isSecondaryApp) {
      path = this.activeTab?.secondaryAppPath
    }
    return path ?? this.$typedStore.public.display.storyPath.toString()
  }

  get originalTabColours() {
    return this.$typedStore.primary.app.config.appConfig?.originalTabColours ?? false
  }

  get isActiveTabForSecondaryApp() {
    return this.activeTab?.secondaryAppPath
  }

  get appForColours() {
    return this.originalTabColours && this.isActiveTabForSecondaryApp
      ? this.$typedStore.secondary
      : this.$typedStore.primary
  }

  get colourVarStyle(): StyleValue {
    const storyBackground = this.activeAppLoaded
      ? this.appForColours.entities.nodes.colour(this.path)
      : this.originalTabColours
      ? this.activeTab?.barColour
      : this.activeTab?.colour

    const storyForeground = this.activeAppLoaded
      ? this.appForColours.app.tabFontColor(this.path, StoryKeys.SIDEBAR_BOT)
      : this.activeTab?.tabFontColor

    return {
      '--story-background': storyBackground ?? 'white',
      '--story-foreground': storyForeground ?? 'white',
    }
  }

  async updated() {
    if (this.$typedStore.activeVisualisation.isSecondaryApp) {
      await this.$typedStore.secondary.app.initialise()
    }
    this.$typedStore.public.size.recalculate()
  }
}
</script>

<style scoped lang="scss">
.mobile-container {
  width: 100%;
  height: 100%;
  overscroll-behavior: none;
  .custom-visualisation {
    margin: 0;
  }

  .top-spinner {
    flex: 1;
    min-height: 0;
    overflow-y: scroll;
  }
}
</style>
